import BaseController from "de/realcore/realseats/controller/BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import { Route$MatchedEvent } from "sap/ui/core/routing/Route";
import Log from "sap/base/Log";
import ApiService from "../src/api/ApiService";
import { Employee } from "../src/types";
import formatter from "../src/utils/formatter";

/**
 * Employee data structure for the EmployeeDetail view
 * We need to outsource this into a separate type file later
 */

export default class EmployeeDetail extends BaseController {
    public formatter = formatter;
    private ApiService: ApiService;

    public onInit(): void {
        this.getOwnerComponent()
            .getRouter()
            .getRoute("EmployeeDetails")
            .attachPatternMatched(this._onRouteMatched.bind(this));
        this.ApiService = new ApiService(this.getOwnerComponent());
    }

    private _onRouteMatched = (oEvent: Route$MatchedEvent): void => {
        const oArgs = oEvent.getParameter("arguments") as { id?: string };
        const sEmployeeId = oArgs.id;

        if (!sEmployeeId) {
            Log.error("No employee ID provided in route");
            this.navTo("EmployeeSearch");
            return;
        }

        void this._loadEmployeeData(sEmployeeId);
    };

    private async _loadEmployeeData(sEmployeeId: string): Promise<void> {
        const oEmployeeModel = this.getOwnerComponent().getModel("EmployeeModel") as JSONModel;
        const oData = oEmployeeModel.getData() as { Employees?: Employee[] } | null;
        const employees = oData?.Employees;

        if (employees) {
            await this._updateEmployeeModelFromData(sEmployeeId, employees);
        } else {
            this.getOwnerComponent()
                .getEventBus()
                .subscribeOnce("EmployeeSearch", "EmployeesLoaded", (): void => {
                    const loadedData = oEmployeeModel.getData() as {
                        Employees?: Employee[];
                    } | null;
                    const loadedEmployees = loadedData?.Employees;

                    if (loadedEmployees) {
                        void this._updateEmployeeModelFromData(sEmployeeId, loadedEmployees);
                    } else {
                        Log.error("Failed to load employee data");
                        this._setFallbackEmployeeData(
                            sEmployeeId,
                            "Unknown Employee",
                            "Data not available"
                        );
                    }
                });
        }
    }

    private async _updateEmployeeModelFromData(
        sEmployeeId: string,
        employeesData: Employee[]
    ): Promise<void> {
        const oEmployeeModel = this.getOwnerComponent().getModel("EmployeeModel") as JSONModel;
        if (!employeesData || employeesData.length === 0) {
            Log.error("No employees data found");
            this._setFallbackEmployeeData(sEmployeeId, "No Data", "Data could not be loaded");
            return;
        }

        const oEmployee = employeesData.find((employee: Employee) => employee.id === sEmployeeId);
        if (oEmployee) {
            oEmployeeModel.setProperty("/selectedEmployee", oEmployee);
            await this._getBookings();
        } else {
            Log.error(`Employee with ID ${sEmployeeId} not found`);
            this._setFallbackEmployeeData(
                sEmployeeId,
                "Employee Not Found",
                "No data available for this ID"
            );
        }
    }

    private _setFallbackEmployeeData(id: string, name: string, position: string): void {
        const oEmployeeModel = this.getOwnerComponent().getModel("EmployeeModel") as JSONModel;
        oEmployeeModel.setProperty("/selectedEmployee", {
            id: id,
            name: name,
            position: position
        });
    }

    private async _getBookings(): Promise<void> {
        const oEmployeeModel = this.getOwnerComponent().getModel("EmployeeModel") as JSONModel;
        const sEmployeeId = oEmployeeModel.getProperty("/selectedEmployee/id") as string;
        const dCurrentDate = new Date();
        dCurrentDate.setHours(0, 0, 0, 0);
        const aBookings = await this.ApiService.booking.fetchBookingsWithFilter(
            sEmployeeId,
            dCurrentDate
        );

        // Filter out past bookings and cancelled bookings - only show ACTIVE (current and future, non-cancelled) bookings
        const aActiveBookings = aBookings.filter((booking: any) => {
            if (!booking.startTime) {
                return false;
            }
            const bookingStart = new Date(booking.startTime);
            // Only show bookings that are:
            // 1. In the future or today (bookingStart >= dCurrentDate)
            // 2. Not cancelled (cancelled === false or undefined/null)
            return bookingStart >= dCurrentDate && !booking.cancelled;
        });

        oEmployeeModel.setProperty("/selectedEmployee/bookings", aActiveBookings);
    }

    public onCloseEmployeeDetails(): void {
        this.navTo("EmployeeSearch");
    }
}
